import { useEffect, useRef, useState } from "react";
import {
  Shield, Clock, CheckCircle2, AlertTriangle, FileText, Plus,
  BadgeCheck, Loader2, ChevronRight, Hourglass, Gavel, Flag,
  MessageSquare, ImageIcon, X, Sparkles, Wifi, WifiOff,
} from "lucide-react";
import { api, apiBaseUrl } from "@/lib/api";

const STATUS_META = {
  open:          { label: "Open",            tone: "bg-amber-500/15 text-warning border-warning/30", icon: Hourglass },
  investigating: { label: "Under Review",    tone: "bg-blue-500/15 text-primary border-primary/30",     icon: Shield },
  evidence_requested: { label: "Awaiting Response", tone: "bg-amber-500/15 text-warning border-warning/30", icon: AlertTriangle },
  resolved:      { label: "Resolved",        tone: "bg-emerald-500/15 text-up border-up/30", icon: CheckCircle2 },
  rejected:      { label: "Rejected",        tone: "bg-line text-muted-foreground border-border", icon: X },
};

const REASON_LABEL = {
  buyer_no_payment:  "Buyer didn't actually pay",
  fake_proof:        "Buyer submitted fake / forged proof",
  chargeback:        "Payment was reversed (chargeback)",
  wrong_amount:      "Wrong amount received",
  seller_no_release: "Seller hasn't released crypto",
  other:             "Other",
};

const TIMELINE_ICON = {
  order_created:     FileText,
  buyer_paid_marked: CheckCircle2,
  appeal_filed:      Flag,
  evidence_added:    ImageIcon,
  admin_assigned:    Gavel,
  resolved:          BadgeCheck,
};

export default function AppealView({ orderId, currentUserId, onAddEvidence }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [wsConnected, setWsConnected] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const wsRef = useRef(null);
  const reloadRef = useRef(null);

  // ----- HTTP loader -----
  const load = async () => {
    try {
      const r = await api.get(`/api/p2p/orders/${orderId}/appeal`);
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };
  reloadRef.current = load;

  useEffect(() => {
    load();
    // Slow safety-net poll (60s) — WS handles the live updates.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [orderId]);

  // ----- WebSocket live updates -----
  useEffect(() => {
    const base = apiBaseUrl();
    const wsUrl = base.replace(/^http/, "ws") + `/api/ws/p2p/order/${orderId}`;
    let alive = true;
    let attempt = 0;

    const open = () => {
      if (!alive) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { setWsConnected(true); attempt = 0; };
      ws.onclose = () => {
        setWsConnected(false);
        if (!alive) return;
        // Exponential backoff capped at 10s
        const delay = Math.min(10000, 800 * Math.pow(1.6, attempt++));
        setTimeout(open, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          // Any of these events should refresh the appeal bundle.
          if (["status", "appeal", "evidence_added", "ocr_completed"].includes(msg.type)
              || ["status", "appeal"].includes(msg.type)) {
            reloadRef.current?.();
          }
        } catch (_) { /* ignore */ }
      };
    };
    open();

    // Heartbeat ping every 25s to keep idle sockets alive
    const ping = setInterval(() => {
      try {
        if (wsRef.current?.readyState === 1) wsRef.current.send("ping");
      } catch (_) {}
    }, 25000);

    return () => {
      alive = false;
      clearInterval(ping);
      try { wsRef.current?.close(); } catch (_) {}
    };
  }, [orderId]);

  // 1s ticker for the SLA countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // ----- AI OCR -----
  const runOcr = async () => {
    setOcrBusy(true); setOcrError("");
    try {
      await api.post(`/api/p2p/orders/${orderId}/receipt-ocr`);
      // WS event will trigger reload, but reload immediately too for snappiness
      load();
    } catch (e) {
      setOcrError(e.response?.data?.detail || e.message);
    } finally {
      setOcrBusy(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-lg bg-down/10 text-down p-3 text-[12px]">
        Couldn't load appeal: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-lg bg-surface-2 p-6 text-center text-[12.5px] text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
        <Loader2 size={14} className="animate-spin" /> Loading appeal details…
      </div>
    );
  }

  const { dispute, order, parties, timeline, sla_target_at, ocr_result } = data;
  const meta = STATUS_META[dispute.status] || STATUS_META.open;
  const StatusIcon = meta.icon;
  const isMine = (uid) => uid === currentUserId;
  const me = parties.buyer.is_self ? "buyer" : "seller";
  const other = me === "buyer" ? "seller" : "buyer";

  const slaText = (() => {
    if (!sla_target_at) return null;
    const remaining = new Date(sla_target_at).getTime() - now;
    if (remaining <= 0) return "SLA exceeded — admin attention overdue";
    const m = Math.floor(remaining / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${m % 60}m left` : `${m}m ${s}s left`;
  })();

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium", timeStyle: "short",
  }) : "—";
  const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4" data-testid="appeal-view">
      {/* ============ HEADER ============ */}
      <div className="rounded-xl border border-border bg-gradient-to-br from-[#0F1730] to-primary text-white p-5 overflow-hidden relative">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] opacity-80">
              <Shield size={12} /> Appeal
            </div>
            <div className="text-[20px] font-semibold mt-1 font-mono break-all">
              {dispute.dispute_id}
            </div>
            <div className="text-[12px] opacity-80 mt-1">
              Filed {fmt(dispute.created_at)} · Reason:{" "}
              <span className="font-semibold">{REASON_LABEL[dispute.reason] || dispute.reason}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border ${meta.tone}`}>
              <StatusIcon size={12} /> {meta.label}
            </span>
            {slaText && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold bg-white/15 backdrop-blur">
                <Clock size={11} /> {slaText}
              </span>
            )}
            <span
              className={
                "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10.5px] font-semibold " +
                (wsConnected ? "bg-emerald-400/25 text-emerald-100" : "bg-white/10 text-white/70")
              }
              title={wsConnected ? "Live updates active" : "Reconnecting…"}
              data-testid="appeal-ws-status"
            >
              {wsConnected ? <Wifi size={10} /> : <WifiOff size={10} />}
              {wsConnected ? "Live" : "Offline"}
            </span>
          </div>
        </div>

        {/* Inline order summary */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
          <KV label="Order" value={<span className="font-mono">{order.order_id.slice(-12)}</span>} />
          <KV label="Crypto" value={`${Number(order.crypto_amount).toFixed(4)} ${order.asset}`} />
          <KV label="Fiat" value={`₹${fmtINR(order.fiat_amount)}`} />
          <KV label="Trade Status" value={(order.status || "").replace(/_/g, " ")} className="capitalize" />
        </div>
      </div>

      {/* ============ TWO COLUMN ============ */}
      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-4">
        {/* LEFT — claim + evidence + trade info */}
        <div className="space-y-4">
          {/* Claim summary */}
          <Card title="Appeal Claim" testid="appeal-claim">
            <div className="px-4 pb-3 pt-1 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                Reason
              </div>
              <div className="text-[13px] text-foreground font-semibold">
                {REASON_LABEL[dispute.reason] || dispute.reason}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-2">
                Filed by · {dispute.raised_by_role}
              </div>
              <div className="text-[13px] text-foreground whitespace-pre-wrap leading-relaxed bg-surface-2 rounded-md p-3 border border-border">
                {dispute.description || <span className="italic text-muted-foreground">No description provided.</span>}
              </div>
            </div>
          </Card>

          {/* Evidence */}
          <Card
            title={`Submitted Evidence (${(dispute.evidence_urls || []).length})`}
            testid="appeal-evidence"
            action={
              dispute.status !== "resolved" && dispute.status !== "rejected" && onAddEvidence ? (
                <button
                  onClick={onAddEvidence}
                  className="px-2.5 py-1 rounded-md brand-gradient text-primary-foreground text-[11.5px] font-semibold inline-flex items-center gap-1"
                  data-testid="appeal-add-evidence"
                >
                  <Plus size={11} /> Add Evidence
                </button>
              ) : null
            }
          >
            <div className="px-4 pb-3">
              {(dispute.evidence_urls || []).length === 0 ? (
                <div className="text-[12px] text-muted-foreground italic py-4 text-center">
                  No evidence has been attached yet.
                </div>
              ) : (
                <EvidenceMiniGrid items={dispute.evidence_urls} />
              )}
            </div>
          </Card>

          {/* Buyer's original receipt + AI OCR verification */}
          {order.payment_proof_url && (
            <Card
              title="Buyer's Original Payment Receipt"
              testid="appeal-orig-receipt"
              action={
                !ocr_result && (
                  <button
                    onClick={runOcr}
                    disabled={ocrBusy}
                    className="px-2.5 py-1 rounded-md bg-gradient-to-r from-violet-600 to-cm-blue text-white text-[11.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="appeal-run-ocr"
                  >
                    {ocrBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                    Verify with AI
                  </button>
                )
              }
            >
              <div className="px-4 pb-3 space-y-3">
                <EvidenceMiniGrid items={[order.payment_proof_url]} />
                {order.buyer_paid_note && (
                  <div className="text-[12px] text-muted-foreground">
                    <span className="font-semibold text-foreground">UTR / reference:</span>{" "}
                    <span className="font-mono">{order.buyer_paid_note}</span>
                  </div>
                )}
                {ocrError && (
                  <div className="text-[11.5px] text-down bg-down/10 rounded-md p-2">
                    {ocrError}
                  </div>
                )}
                {ocr_result && (
                  <OcrResult result={ocr_result} onRerun={runOcr} rerunning={ocrBusy} />
                )}
              </div>
            </Card>
          )}

          {/* Trade information */}
          <Card title="Trade Information" testid="appeal-trade-info">
            <dl className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
              <Row label="Crypto Asset" value={order.asset} />
              <Row label="Trade Amount" value={`${Number(order.crypto_amount).toFixed(6)} ${order.asset}`} />
              <Row label="Fiat Amount" value={`₹${fmtINR(order.fiat_amount)}`} />
              <Row label="Price" value={`₹${order.price} / ${order.asset}`} />
              <Row label="Payment Method" value={order.payment_method_snapshot?.type || "—"} />
              {order.payment_method_snapshot?.upi_id && (
                <Row label="UPI ID" value={order.payment_method_snapshot.upi_id} mono />
              )}
              {order.payment_method_snapshot?.account_number && (
                <Row label="Account" value={order.payment_method_snapshot.account_number} mono />
              )}
              <Row label="Order Created" value={fmt(order.created_at)} />
              <Row label="Payment Marked" value={fmt(order.buyer_marked_paid_at)} />
            </dl>
          </Card>
        </div>

        {/* RIGHT — parties + timeline + compliance */}
        <div className="space-y-4">
          {/* Parties */}
          <Card title="Parties" testid="appeal-parties">
            <div className="px-4 pb-3 space-y-3">
              <PartyCard role="Buyer" p={parties.buyer} />
              <PartyCard role="Seller" p={parties.seller} />
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Activity Timeline" testid="appeal-timeline">
            <div className="px-4 pb-3">
              <ol className="relative border-l-2 border-border ml-2 space-y-3 pt-1">
                {timeline.map((t, i) => {
                  const Icon = TIMELINE_ICON[t.key] || ChevronRight;
                  const isResolved = t.key === "resolved";
                  return (
                    <li key={i} className="ml-4 relative" data-testid={`appeal-timeline-${i}`}>
                      <span className={
                        "absolute -left-[24px] top-0 w-4 h-4 rounded-full grid place-items-center " +
                        (isResolved
                          ? "bg-up text-white"
                          : "brand-gradient text-primary-foreground")
                      }>
                        <Icon size={9} />
                      </span>
                      <div className="text-[12.5px] text-foreground font-semibold leading-tight">
                        {t.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {fmt(t.at)}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </Card>

          {/* Resolution panel — only after resolved */}
          {dispute.status === "resolved" && (
            <Card title="Resolution" testid="appeal-resolution">
              <div className="px-4 pb-3 space-y-2 text-[12.5px]">
                <Row label="Outcome" value={
                  <span className="font-semibold text-foreground capitalize">
                    {(dispute.resolution || "").replace(/_/g, " ")}
                  </span>
                } />
                <Row label="Resolved by" value={dispute.resolved_by_admin_id || "Admin"} />
                <Row label="When" value={fmt(dispute.resolved_at)} />
                {dispute.resolution_note && (
                  <div className="mt-2 text-[12px] text-muted-foreground italic bg-surface-2 rounded-md p-2.5 border border-border">
                    "{dispute.resolution_note}"
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Compliance / warnings */}
          <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[11.5px] text-warning space-y-1.5">
            <div className="font-semibold inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Compliance Notice
            </div>
            <ul className="list-disc pl-4 space-y-0.5 opacity-90">
              <li>False appeals may lead to account suspension.</li>
              <li>Do not cancel an order if you've already sent payment.</li>
              <li>All actions in this appeal are logged for audit.</li>
              <li>Admins typically rule within <b>2 hours</b> of filing.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer banner — explanatory */}
      {dispute.status !== "resolved" && (
        <div className="rounded-lg bg-primary/10 text-primary p-3 text-[12.5px] flex items-start gap-2">
          <MessageSquare size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Continue using the chat on the right to communicate. Admins are added to the
            conversation once the case is claimed and may post messages here.
          </span>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- subcomponents ----------------------------- */

function Card({ title, testid, action, children }) {
  return (
    <div className="rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <span className="text-[11.5px] uppercase tracking-wider font-semibold text-muted-foreground">
          {title}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, className = "" }) {
  return (
    <div className="text-[11px] uppercase tracking-wider opacity-80">
      <div className="text-white/70 font-semibold text-[10.5px]">{label}</div>
      <div className={`text-white text-[14px] font-semibold mt-0.5 normal-case tracking-normal ${className}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`text-foreground font-medium text-right ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}

function PartyCard({ role, p }) {
  const initial = (p.nickname || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={
      "rounded-lg border p-2.5 flex items-center gap-2.5 " +
      (p.is_self ? "border-primary bg-primary/5" : "border-border bg-surface-2")
    }>
      <div
        className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold text-[13px] flex-shrink-0"
        style={{ background: "linear-gradient(120deg, oklch(var(--brand-emerald)), oklch(var(--brand-lime)))" }}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{role}</span>
          {p.is_self && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 rounded brand-gradient text-primary-foreground">YOU</span>
          )}
        </div>
        <div className="text-[13px] font-semibold text-foreground truncate">{p.nickname}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {p.kyc_verified && <Tag ok>KYC</Tag>}
          {p.email_verified && <Tag ok>Email</Tag>}
          {p.phone_verified && <Tag ok>Phone</Tag>}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {p.trades_total} trades · {Number(p.completion_rate_30d || 0).toFixed(0)}% completion
        </div>
      </div>
    </div>
  );
}

function Tag({ ok, children }) {
  return (
    <span className={
      "inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[10px] font-semibold " +
      (ok ? "bg-up/15 text-up" : "bg-surface-2 text-muted-foreground")
    }>
      <BadgeCheck size={9} />
      {children}
    </span>
  );
}

function OcrResult({ result, onRerun, rerunning }) {
  const f = result.fields || {};
  const checks = result.checks || {};
  const conf = Number(f.confidence || 0);
  const confPct = Math.round(conf * 100);
  const confTone = conf >= 0.7 ? "text-up bg-up/10"
                  : conf >= 0.4 ? "text-warning bg-warning/10"
                  : "text-down bg-down/10";

  const fmtINR = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const fmtTs = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div
      className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 via-card to-blue-50 p-3 space-y-2.5"
      data-testid="appeal-ocr-result"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-violet-800 inline-flex items-center gap-1.5">
          <Sparkles size={12} /> AI Receipt Verification
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${confTone}`}
                data-testid="ocr-confidence">
            {confPct}% confidence
          </span>
          <button
            onClick={onRerun}
            disabled={rerunning}
            className="text-[10.5px] text-violet-700 underline font-semibold disabled:opacity-50"
          >
            {rerunning ? "Re-running…" : "Re-run"}
          </button>
        </div>
      </div>

      {/* Field check rows */}
      <div className="space-y-1" data-testid="ocr-checks">
        <CheckRow label="Amount"   ok={checks.amount?.ok}   note={checks.amount?.note} />
        <CheckRow label="UTR"      ok={checks.utr?.ok}      note={checks.utr?.note} />
        <CheckRow label="Receiver" ok={checks.receiver?.ok} note={checks.receiver?.note} />
        <CheckRow label="Status"   ok={checks.status?.ok}   note={checks.status?.note} />
      </div>

      {/* Extracted raw fields (compact) */}
      <details className="text-[11.5px] text-muted-foreground">
        <summary className="cursor-pointer font-semibold hover:text-foreground select-none">
          Extracted fields
        </summary>
        <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 pl-2 border-l-2 border-violet-200">
          <ExField label="Amount"     value={fmtINR(f.amount)} />
          <ExField label="UTR"        value={f.utr || "—"} mono />
          <ExField label="Time"       value={fmtTs(f.timestamp_iso)} />
          <ExField label="Status"     value={f.status || "—"} />
          <ExField label="Receiver"   value={f.receiver_name || "—"} />
          <ExField label="UPI"        value={f.receiver_upi || "—"} mono />
        </dl>
      </details>

      {f.raw_text && (
        <div className="text-[11px] italic text-muted-foreground opacity-80">
          AI summary: {f.raw_text}
        </div>
      )}
    </div>
  );
}

function CheckRow({ label, ok, note }) {
  return (
    <div className="flex items-start gap-2 text-[12px]">
      <span className={
        "mt-0.5 flex-shrink-0 w-4 h-4 rounded-full grid place-items-center " +
        (ok ? "bg-up text-white" : "bg-warning text-white")
      }>
        {ok ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
      </span>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-foreground">{label}:</span>{" "}
        <span className={ok ? "text-up" : "text-warning"}>{note || "—"}</span>
      </div>
    </div>
  );
}

function ExField({ label, value, mono }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`text-foreground font-medium text-right truncate ${mono ? "font-mono" : ""}`} title={value}>
        {value}
      </dd>
    </>
  );
}

function EvidenceMiniGrid({ items }) {
  const [lightbox, setLightbox] = useState(null);
  const classify = (u) => {
    if (!u) return "link";
    if (u.startsWith("data:image/")) return "image";
    if (u.startsWith("data:video/")) return "video";
    if (u.startsWith("data:application/pdf")) return "pdf";
    return "link";
  };
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {items.map((u, i) => {
          const k = classify(u);
          return (
            <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-border bg-surface-2 group hover:border-primary transition">
              {k === "image" && (
                <button onClick={() => setLightbox(u)} className="block w-full h-full">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              )}
              {k === "video" && <video src={u} controls className="w-full h-full object-contain bg-black" />}
              {k === "pdf" && (
                <a href={u} target="_blank" rel="noreferrer" download={`evidence-${i+1}.pdf`}
                  className="flex flex-col items-center justify-center w-full h-full text-down gap-0.5">
                  <span className="text-[16px] font-semibold">PDF</span>
                  <span className="text-[10px] text-muted-foreground">Open</span>
                </a>
              )}
              {k === "link" && (
                <a href={u} target="_blank" rel="noreferrer"
                  className="flex flex-col items-center justify-center w-full h-full text-primary gap-0.5 px-1.5 text-center">
                  <span className="text-[12px] font-semibold">URL</span>
                  <span className="text-[9px] text-muted-foreground break-all line-clamp-3">{u}</span>
                </a>
              )}
              <div className="absolute top-1 right-1 px-1 py-0 rounded bg-black/65 text-white text-[8.5px] font-semibold uppercase tracking-wider">
                {k}
              </div>
            </div>
          );
        })}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/85 grid place-items-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="full" className="max-w-[92vw] max-h-[92vh] rounded-md shadow-2xl" />
        </div>
      )}
    </>
  );
}
